// crashBreadcrumbs — a lightweight, in-memory trail of "what the user did right
// before the crash" so the ErrorBoundary can report it. Capture-phase listeners,
// a bounded ring buffer, zero deps, no network, no storage. Installed once at app
// boot; read by App.tsx's ErrorBoundary when it builds the crash report.

export interface Breadcrumb {
  /** ms since page load (performance.now). */
  t: number;
  type: 'click' | 'nav' | 'key' | 'input';
  label: string;
}

const MAX = 25;
const trail: Breadcrumb[] = [];
let installed = false;

function push(type: Breadcrumb['type'], label: string) {
  trail.push({ t: Math.round(performance.now()), type, label: label.slice(0, 160) });
  if (trail.length > MAX) trail.shift();
}

// Build a human-readable descriptor for a clicked/focused element: the nearest
// meaningful interactive ancestor, with whatever identity we can scrape (label,
// text, data-slot, id, href). This is what turns "click" into "clicked the
// Filter button" in the report.
function describeElement(el: Element | null): string {
  if (!el) return '(unknown)';
  const interactive = el.closest(
    'button, a, [role="button"], [role="menuitem"], [role="menuitemcheckbox"], [role="tab"], [role="option"], input, select, textarea, [data-slot]'
  );
  const target = (interactive as HTMLElement) ?? (el as HTMLElement);
  const tag = target.tagName.toLowerCase();
  const id = target.id;
  const slot = target.getAttribute('data-slot');
  const role = target.getAttribute('role');
  const name = target.getAttribute('name');
  const aria = target.getAttribute('aria-label');
  const title = target.getAttribute('title');
  const href = tag === 'a' ? (target as HTMLAnchorElement).getAttribute('href') : null;
  const text = (target.textContent ?? '').replace(/\s+/g, ' ').trim().slice(0, 60);

  const sel = [tag];
  if (id) sel.push(`#${id}`);
  if (slot) sel.push(`[data-slot=${slot}]`);
  if (role) sel.push(`[role=${role}]`);
  if (name && tag !== 'a') sel.push(`[name=${name}]`);

  const descriptor = aria || title || text || name || href || '';
  return descriptor ? `${sel.join('')} "${descriptor}"` : sel.join('');
}

export function installCrashBreadcrumbs() {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  // Capture phase so we record even if a handler later calls stopPropagation.
  window.addEventListener('click', (e) => push('click', describeElement(e.target as Element)), { capture: true });

  window.addEventListener('keydown', (e) => {
    // Only navigation / shortcut keys — never log raw text the user types.
    const meaningful = ['Enter', 'Escape', 'Tab', 'ArrowUp', 'ArrowDown'].includes(e.key) || e.ctrlKey || e.metaKey;
    if (!meaningful) return;
    const combo = [e.ctrlKey && 'Ctrl', e.metaKey && 'Cmd', e.shiftKey && 'Shift', e.altKey && 'Alt', e.key]
      .filter(Boolean)
      .join('+');
    push('key', combo);
  }, { capture: true });

  // Record changes to form fields by identity only (no value).
  window.addEventListener('change', (e) => push('input', describeElement(e.target as Element)), { capture: true });

  // SPA route changes — patch pushState so client navigations show up too.
  const record = () => push('nav', location.pathname + location.search + location.hash);
  window.addEventListener('popstate', record);
  const origPush = history.pushState.bind(history);
  history.pushState = function (this: History, ...args: Parameters<History['pushState']>) {
    const r = origPush(...args);
    record();
    return r;
  };
  record(); // initial route
}

/** Pretty multi-line trail for the crash report; oldest → newest. */
export function formatBreadcrumbs(): string {
  if (!trail.length) return '(none recorded)';
  return trail
    .map((b) => `  +${(b.t / 1000).toFixed(1).padStart(6)}s  ${b.type.padEnd(5)}  ${b.label}`)
    .join('\n');
}

/** The single most recent interaction, e.g. "clicked just before the crash". */
export function lastAction(): string {
  const last = trail[trail.length - 1];
  return last ? `${last.type} ${last.label}` : '(none)';
}
